import * as mockData from './mockData';
import * as demoStorage from './demoStorage';

// Flag da proverimo da li je već inicijalizovano u ovoj sesiji
let demoStorageInitialized = false;

// Mock API handler za demo mode
export const handleMockApiCall = async (endpoint, options = {}) => {
  // Inicijalizuj demo storage sa mock podacima samo jednom po sesiji
  // Resetuj se na početne vrednosti svaki put kada se aplikacija učita
  if (!demoStorageInitialized) {
    demoStorage.initializeDemoStorage(mockData);
    demoStorageInitialized = true;
  }
  
  // Simuliraj malu delay za realističnost
  await new Promise(resolve => setTimeout(resolve, 300));

  const method = options.method || 'GET';
  const body = options.body ? JSON.parse(options.body) : null;

  // Route matching za mock podatke
  if (endpoint.includes('/tenants') && method === 'GET') {
    const tenant = demoStorage.getDemoTenant();
    if (endpoint.includes('/tenants/getTenantsByFilter')) {
      // Get tenants by filter
      return { tenants: [tenant] };
    }
    if (endpoint.includes('/tenants/external-registration/')) {
      // External registration info
      return tenant;
    }
    if (endpoint.includes('/tenants/')) {
      // Get tenant by ID
      const tenantId = endpoint.split('/tenants/')[1]?.split('?')[0];
      if (tenantId === 'demo-tenant-1' || tenantId === tenant._id) {
        return tenant;
      }
      return tenant; // Fallback
    }
    // Get all tenants
    return { tenants: [tenant] };
  }

  if (endpoint.includes('/tenants') && method === 'PUT') {
    // Update tenant - ažuriraj u localStorage
    const tenantId = endpoint.split('/tenants/')[1]?.split('?')[0];
    const updated = demoStorage.updateDemoTenant({
      ...body,
      _id: tenantId || demoStorage.getDemoTenant()._id
    });
    return updated;
  }

  if (endpoint.includes('/tenants') && method === 'PATCH') {
    // Update tenant subscription or other fields - simuliraj uspeh
    const tenantId = endpoint.split('/tenants/')[1]?.split('/')[0];
    return {
      ...mockData.mockTenant,
      ...body,
      _id: tenantId || mockData.mockTenant._id,
      updatedAt: new Date().toISOString()
    };
  }

  if (endpoint.includes('/tenants/upload-image') && method === 'POST') {
    // Upload tenant logo - simuliraj uspeh
    return {
      logoUrl: 'https://via.placeholder.com/200',
      message: 'Logo uploaded successfully'
    };
  }

  if (endpoint.includes('/services') && method === 'GET') {
    const allServices = demoStorage.getDemoServices();
    const allCategories = demoStorage.getDemoCategories();
    
    // Helper funkcija za normalizaciju categoryId u uslugama
    const normalizeServiceCategory = (service) => {
      if (!service.categoryId) return service;
      
      const categoryIdValue = typeof service.categoryId === 'object' 
        ? service.categoryId._id 
        : service.categoryId;
      
      const category = allCategories.find(c => c._id === categoryIdValue);
      
      if (category) {
        return {
          ...service,
          categoryId: {
            _id: category._id,
            name: category.name
          }
        };
      }
      
      // Ako kategorija ne postoji, zadrži originalnu vrednost
      return service;
    };
    
    // Normalizuj sve usluge
    const normalizedServices = allServices.map(normalizeServiceCategory);
    
    if (endpoint.includes('/services/tenant/')) {
      // Get services by tenant
      return { 
        services: normalizedServices,
        total: normalizedServices.length,
        page: 1,
        limit: 10
      };
    }
    if (endpoint.includes('/services/category/')) {
      // Get services by category
      const categoryId = endpoint.split('/services/category/')[1]?.split('?')[0];
      const filtered = categoryId 
        ? normalizedServices.filter(s => {
            const serviceCategoryId = s.categoryId?._id || s.categoryId;
            return String(serviceCategoryId) === String(categoryId);
          })
        : normalizedServices;
      return { services: filtered };
    }
    if (endpoint.includes('/services/')) {
      // Get service by ID
      const serviceId = endpoint.split('/services/')[1]?.split('?')[0];
      const service = normalizedServices.find(s => s._id === serviceId);
      return service || normalizedServices[0] || null;
    }
    if (endpoint.includes('/services/categories/list')) {
      // Get service categories list
      return demoStorage.getDemoCategories();
    }
    return { services: normalizedServices };
  }

  if (endpoint.includes('/services') && method === 'POST') {
    // Create service - čuvaj u localStorage
    // Normalizuj categoryId da bude objekat sa _id i name iz kategorije
    let normalizedBody = { ...body };
    if (body.categoryId) {
      const allCategories = demoStorage.getDemoCategories();
      const categoryIdValue = typeof body.categoryId === 'object' ? body.categoryId._id : body.categoryId;
      const category = allCategories.find(c => c._id === categoryIdValue);
      if (category) {
        normalizedBody.categoryId = {
          _id: category._id,
          name: category.name
        };
      } else {
        // Ako kategorija ne postoji, koristi originalnu vrednost
        normalizedBody.categoryId = typeof body.categoryId === 'object' ? body.categoryId : { _id: body.categoryId };
      }
    }
    const newService = demoStorage.addDemoService(normalizedBody);
    return newService;
  }

  if (endpoint.includes('/services') && method === 'PUT') {
    // Update service - ažuriraj u localStorage
    const serviceId = endpoint.split('/services/')[1]?.split('?')[0];
    // Normalizuj categoryId da bude objekat sa _id i name iz kategorije
    let normalizedBody = { ...body };
    if (body.categoryId) {
      const allCategories = demoStorage.getDemoCategories();
      const categoryIdValue = typeof body.categoryId === 'object' ? body.categoryId._id : body.categoryId;
      const category = allCategories.find(c => c._id === categoryIdValue);
      if (category) {
        normalizedBody.categoryId = {
          _id: category._id,
          name: category.name
        };
      } else {
        // Ako kategorija ne postoji, koristi originalnu vrednost
        normalizedBody.categoryId = typeof body.categoryId === 'object' ? body.categoryId : { _id: body.categoryId };
      }
    }
    const updated = demoStorage.updateDemoService(serviceId, normalizedBody);
    return updated || { _id: serviceId, ...normalizedBody };
  }

  if (endpoint.includes('/services') && method === 'DELETE') {
    // Delete service - obriši iz localStorage
    const serviceId = endpoint.split('/services/')[1]?.split('?')[0];
    demoStorage.deleteDemoService(serviceId);
    return { success: true };
  }

  if (endpoint.includes('/service-categories') && method === 'GET') {
    const allCategories = demoStorage.getDemoCategories();
    if (endpoint.includes('/service-categories/tenant/')) {
      // Get categories by tenant
      return {
        categories: allCategories,
        total: allCategories.length,
        page: 1,
        limit: 10
      };
    }
    if (endpoint.includes('/service-categories/')) {
      // Get category by ID
      const categoryId = endpoint.split('/service-categories/')[1]?.split('?')[0];
      return allCategories.find(c => c._id === categoryId) || allCategories[0] || null;
    }
    return allCategories;
  }

  if (endpoint.includes('/service-categories') && method === 'POST') {
    // Create category - čuvaj u localStorage
    const newCategory = demoStorage.addDemoCategory(body);
    return newCategory;
  }

  if (endpoint.includes('/service-categories') && method === 'PUT') {
    // Update category - ažuriraj u localStorage
    const categoryId = endpoint.split('/service-categories/')[1]?.split('?')[0];
    const updated = demoStorage.updateDemoCategory(categoryId, body);
    return updated || { _id: categoryId, ...body };
  }

  if (endpoint.includes('/service-categories') && method === 'DELETE') {
    // Delete category - obriši iz localStorage
    const categoryId = endpoint.split('/service-categories/')[1]?.split('?')[0];
    demoStorage.deleteDemoCategory(categoryId);
    return { success: true };
  }

  if (endpoint.includes('/exceptions/available-days') && method === 'GET') {
    return mockData.mockAvailableDays;
  }

  if (endpoint.includes('/exceptions/available-slots') && method === 'GET') {
    const urlParams = new URLSearchParams(endpoint.split('?')[1] || '');
    const date = urlParams.get('date');
    return mockData.getMockAvailableSlots(date);
  }

  if (endpoint.includes('/tenant-users') && method === 'GET') {
    if (endpoint.includes('/getTenantsForUser')) {
      // Get tenants for user (demo customer)
      const tenant = demoStorage.getDemoTenant();
      return [{
        ...tenant,
        approved: true,
        userType: 'customer'
      }];
    }
    // Get users/employees - proveri query parametre da li se traže zaposleni ili korisnici
    const urlParams = new URLSearchParams(endpoint.split('?')[1] || '');
    const isEmployeesPage = urlParams.get('isEmployeesPage') === 'true' || 
                           urlParams.get('isEmployeesPage') === '1';
    const includeEmployees = urlParams.get('includeEmployees') === 'true' || 
                            urlParams.get('includeEmployees') === '1';
    
    if (isEmployeesPage) {
      // Vrati samo zaposlene
      const employees = demoStorage.getDemoEmployees();
      return { 
        users: employees,
        total: employees.length,
        page: 1,
        limit: 10
      };
    } else if (includeEmployees) {
      // Vrati i zaposlene i korisnike (za AppointmentBooking i slične slučajeve)
      const employees = demoStorage.getDemoEmployees();
      const customers = demoStorage.getDemoCustomers();
      const allUsers = [...employees, ...customers];
      return { 
        users: allUsers,
        total: allUsers.length,
        page: 1,
        limit: 10
      };
    } else {
      // Vrati samo korisnike (customers) - ne uključuj zaposlene
      const customers = demoStorage.getDemoCustomers();
      return { 
        users: customers,
        total: customers.length,
        page: 1,
        limit: 10
      };
    }
  }

  if (endpoint.includes('/tenant-users') && method === 'POST') {
    if (endpoint.includes('/approve-user')) {
      // Approve user - simuliraj uspeh
      return { success: true, message: 'User approved' };
    }
    if (endpoint.includes('/reject-user')) {
      // Reject user - simuliraj uspeh
      return { success: true, message: 'User rejected' };
    }
    // Create new user/employee - čuvaj u localStorage
    const userType = body.userType || 'customer';
    if (userType === 'employee' || userType === 'tenant_admin') {
      const newEmployee = demoStorage.addDemoEmployee({
        ...body,
        _id: body._id || `demo-employee-${Date.now()}`,
        createdAt: new Date().toISOString()
      });
      return newEmployee;
    } else {
      // Customer
      const newCustomer = demoStorage.addDemoCustomer({
        ...body,
        _id: body._id || `demo-customer-${Date.now()}`,
        createdAt: new Date().toISOString()
      });
      return newCustomer;
    }
  }

  if (endpoint.includes('/tenant-users') && method === 'PUT') {
    // Update user - ažuriraj u localStorage
    const userId = endpoint.split('/tenant-users/')[1]?.split('?')[0];
    const userType = body.userType || 'customer';
    
    if (userType === 'employee' || userType === 'tenant_admin') {
      const updated = demoStorage.updateDemoEmployee(userId, body);
      return updated || { _id: userId, ...body, updatedAt: new Date().toISOString() };
    } else {
      const updated = demoStorage.updateDemoCustomer(userId, body);
      return updated || { _id: userId, ...body, updatedAt: new Date().toISOString() };
    }
  }

  if (endpoint.includes('/tenant-users') && method === 'DELETE') {
    // Delete user - obriši iz localStorage
    const userId = endpoint.split('/tenant-users/')[1]?.split('?')[0];
    // Pokušaj da obrišeš iz oba (employee ili customer)
    demoStorage.deleteDemoEmployee(userId);
    demoStorage.deleteDemoCustomer(userId);
    return { success: true, message: 'User deleted' };
  }

  if (endpoint.includes('/lookup') && method === 'GET') {
    if (endpoint.includes('/_tip_klijenta')) {
      // Client types
      return [
        { _id: 'demo-type-1', name: 'Frizerski salon', description: 'Frizerski saloni' },
        { _id: 'demo-type-2', name: 'Nokti', description: 'Saloni za nokte' }
      ];
    }
    return [];
  }

  if (endpoint.includes('/appointments') && method === 'GET') {
    const allAppointments = demoStorage.getDemoAppointments();
    
    // Helper funkcija za filtriranje termina na osnovu datuma
    const filterAppointmentsByDate = (appointments, dateFrom, dateTo) => {
      if (!dateFrom && !dateTo) return appointments;
      
      return appointments.filter(apt => {
        const aptDate = new Date(apt.appointmentDate);
        aptDate.setHours(0, 0, 0, 0);
        
        if (dateFrom) {
          const fromDate = new Date(dateFrom);
          fromDate.setHours(0, 0, 0, 0);
          if (aptDate < fromDate) return false;
        }
        
        if (dateTo) {
          const toDate = new Date(dateTo);
          toDate.setHours(23, 59, 59, 999);
          if (aptDate > toDate) return false;
        }
        
        return true;
      });
    };
    
    // Helper funkcija za filtriranje termina na osnovu statusa
    const filterAppointmentsByStatus = (appointments, status) => {
      if (!status || status === 'all') return appointments;
      return appointments.filter(apt => apt.status === status);
    };
    
    // Helper funkcija za filtriranje termina na osnovu imena klijenta
    const filterAppointmentsByClientName = (appointments, clientName) => {
      if (!clientName || !clientName.trim()) return appointments;
      const searchTerm = clientName.toLowerCase().trim();
      return appointments.filter(apt => {
        const customer = apt.customerId;
        if (!customer) return false;
        const firstName = (customer.firstName || '').toLowerCase();
        const lastName = (customer.lastName || '').toLowerCase();
        const fullName = `${firstName} ${lastName}`.trim();
        return fullName.includes(searchTerm) || firstName.includes(searchTerm) || lastName.includes(searchTerm);
      });
    };
    
    // Proveri da li je opšti endpoint /appointments (bez dodatnih putanja)
    if (endpoint === '/appointments' || endpoint.match(/^\/appointments\?/)) {
      // GetAllAppointments - vrati sve termine sa paginacijom
      const urlParams = new URLSearchParams(endpoint.split('?')[1] || '');
      const page = parseInt(urlParams.get('page') || '1', 10);
      const limit = parseInt(urlParams.get('limit') || '10', 10);
      const dateFrom = urlParams.get('dateFrom');
      const dateTo = urlParams.get('dateTo');
      const status = urlParams.get('status');
      const clientName = urlParams.get('clientName');
      
      // Primeni filtere
      let filtered = allAppointments;
      filtered = filterAppointmentsByDate(filtered, dateFrom, dateTo);
      filtered = filterAppointmentsByStatus(filtered, status);
      filtered = filterAppointmentsByClientName(filtered, clientName);
      
      // Sortiranje
      const sortBy = urlParams.get('sortBy');
      const sortOrder = urlParams.get('sortOrder') || 'asc';
      if (sortBy) {
        filtered.sort((a, b) => {
          let aVal, bVal;
          if (sortBy === 'appointmentDate') {
            aVal = new Date(a.appointmentDate);
            bVal = new Date(b.appointmentDate);
          } else if (sortBy === 'totalPrice') {
            aVal = a.totalPrice || 0;
            bVal = b.totalPrice || 0;
          } else if (sortBy === 'createdAt') {
            aVal = new Date(a.createdAt || a.appointmentDate);
            bVal = new Date(b.createdAt || b.appointmentDate);
          } else {
            return 0;
          }
          
          if (sortOrder === 'desc') {
            return bVal > aVal ? 1 : bVal < aVal ? -1 : 0;
          } else {
            return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
          }
        });
      }
      
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedAppointments = filtered.slice(startIndex, endIndex);
      
      return {
        appointments: paginatedAppointments,
        total: filtered.length,
        page: page,
        limit: limit,
        totalPages: Math.ceil(filtered.length / limit)
      };
    }
    
    if (endpoint.includes('/appointments/stats/overview')) {
      // Get appointment stats overview
      return {
        totalAppointments: allAppointments.length,
        confirmedAppointments: allAppointments.filter(a => a.status === 'confirmed').length,
        pendingAppointments: allAppointments.filter(a => a.status === 'pending').length,
        cancelledAppointments: allAppointments.filter(a => a.status === 'cancelled').length,
        totalRevenue: allAppointments.reduce((sum, a) => sum + (a.totalPrice || 0), 0)
      };
    }
    if (endpoint.includes('/appointments/statistics')) {
      // Get appointment statistics
      return {
        total: allAppointments.length,
        confirmed: allAppointments.filter(a => a.status === 'confirmed').length,
        pending: allAppointments.filter(a => a.status === 'pending').length,
        cancelled: allAppointments.filter(a => a.status === 'cancelled').length,
        revenue: allAppointments.reduce((sum, a) => sum + (a.totalPrice || 0), 0)
      };
    }
    if (endpoint.includes('/appointments/tenant/')) {
      // GetAppointmentsByTenant - filtriraj na osnovu query parametara
      const urlParams = new URLSearchParams(endpoint.split('?')[1] || '');
      const page = parseInt(urlParams.get('page') || '1', 10);
      const limit = parseInt(urlParams.get('limit') || '10', 10);
      const dateFrom = urlParams.get('dateFrom');
      const dateTo = urlParams.get('dateTo');
      const status = urlParams.get('status');
      const clientName = urlParams.get('clientName');
      
      // Primeni filtere
      let filtered = allAppointments;
      filtered = filterAppointmentsByDate(filtered, dateFrom, dateTo);
      filtered = filterAppointmentsByStatus(filtered, status);
      filtered = filterAppointmentsByClientName(filtered, clientName);
      
      // Sortiranje
      const sortBy = urlParams.get('sortBy');
      const sortOrder = urlParams.get('sortOrder') || 'asc';
      if (sortBy) {
        filtered.sort((a, b) => {
          let aVal, bVal;
          if (sortBy === 'appointmentDate') {
            aVal = new Date(a.appointmentDate);
            bVal = new Date(b.appointmentDate);
          } else if (sortBy === 'totalPrice') {
            aVal = a.totalPrice || 0;
            bVal = b.totalPrice || 0;
          } else if (sortBy === 'createdAt') {
            aVal = new Date(a.createdAt || a.appointmentDate);
            bVal = new Date(b.createdAt || b.appointmentDate);
          } else {
            return 0;
          }
          
          if (sortOrder === 'desc') {
            return bVal > aVal ? 1 : bVal < aVal ? -1 : 0;
          } else {
            return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
          }
        });
      }
      
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginated = filtered.slice(startIndex, endIndex);
      
      return {
        appointments: paginated,
        total: filtered.length,
        page: page,
        limit: limit,
        totalPages: Math.ceil(filtered.length / limit)
      };
    }
    if (endpoint.includes('/appointments/customer/')) {
      // Filtriraj termine samo za tog korisnika
      // Endpoint format: /appointments/customer/:customerId?page=1&limit=10&tenantId=...
      const pathParts = endpoint.split('/appointments/customer/')[1];
      const customerId = pathParts?.split('?')[0]?.split('/')[0];
      
      if (customerId) {
        let filtered = allAppointments.filter(apt => {
          const aptCustomerId = apt.customerId?._id || apt.customerId;
          return String(aptCustomerId) === String(customerId);
        });
        
        // Proveri query parametre za paginaciju i filtere
        const urlParams = new URLSearchParams(endpoint.split('?')[1] || '');
        const page = parseInt(urlParams.get('page') || '1', 10);
        const limit = parseInt(urlParams.get('limit') || '10', 10);
        const dateFrom = urlParams.get('dateFrom');
        const dateTo = urlParams.get('dateTo');
        const status = urlParams.get('status');
        const clientName = urlParams.get('clientName');
        
        // Primeni filtere
        filtered = filterAppointmentsByDate(filtered, dateFrom, dateTo);
        filtered = filterAppointmentsByStatus(filtered, status);
        filtered = filterAppointmentsByClientName(filtered, clientName);
        
        // Sortiranje
        const sortBy = urlParams.get('sortBy');
        const sortOrder = urlParams.get('sortOrder') || 'asc';
        if (sortBy) {
          filtered.sort((a, b) => {
            let aVal, bVal;
            if (sortBy === 'appointmentDate') {
              aVal = new Date(a.appointmentDate);
              bVal = new Date(b.appointmentDate);
            } else if (sortBy === 'totalPrice') {
              aVal = a.totalPrice || 0;
              bVal = b.totalPrice || 0;
            } else if (sortBy === 'createdAt') {
              aVal = new Date(a.createdAt || a.appointmentDate);
              bVal = new Date(b.createdAt || b.appointmentDate);
            } else {
              return 0;
            }
            
            if (sortOrder === 'desc') {
              return bVal > aVal ? 1 : bVal < aVal ? -1 : 0;
            } else {
              return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
            }
          });
        }
        
        if (limit && limit < 99999) {
          // Ako ima limit, primeni paginaciju
          const startIndex = (page - 1) * limit;
          const endIndex = startIndex + limit;
          const paginated = filtered.slice(startIndex, endIndex);
          return {
            appointments: paginated,
            total: filtered.length,
            page: page,
            limit: limit,
            totalPages: Math.ceil(filtered.length / limit)
          };
        }
        
        return { appointments: filtered };
      }
      
      return { appointments: [] };
    }
    if (endpoint.includes('/appointments/employee/')) {
      // GetAppointmentsByEmployee - filtriraj na osnovu query parametara
      const urlParams = new URLSearchParams(endpoint.split('?')[1] || '');
      const page = parseInt(urlParams.get('page') || '1', 10);
      const limit = parseInt(urlParams.get('limit') || '10', 10);
      const dateFrom = urlParams.get('dateFrom');
      const dateTo = urlParams.get('dateTo');
      const status = urlParams.get('status');
      const clientName = urlParams.get('clientName');
      
      // Primeni filtere
      let filtered = allAppointments;
      filtered = filterAppointmentsByDate(filtered, dateFrom, dateTo);
      filtered = filterAppointmentsByStatus(filtered, status);
      filtered = filterAppointmentsByClientName(filtered, clientName);
      
      // Sortiranje
      const sortBy = urlParams.get('sortBy');
      const sortOrder = urlParams.get('sortOrder') || 'asc';
      if (sortBy) {
        filtered.sort((a, b) => {
          let aVal, bVal;
          if (sortBy === 'appointmentDate') {
            aVal = new Date(a.appointmentDate);
            bVal = new Date(b.appointmentDate);
          } else if (sortBy === 'totalPrice') {
            aVal = a.totalPrice || 0;
            bVal = b.totalPrice || 0;
          } else if (sortBy === 'createdAt') {
            aVal = new Date(a.createdAt || a.appointmentDate);
            bVal = new Date(b.createdAt || b.appointmentDate);
          } else {
            return 0;
          }
          
          if (sortOrder === 'desc') {
            return bVal > aVal ? 1 : bVal < aVal ? -1 : 0;
          } else {
            return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
          }
        });
      }
      
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginated = filtered.slice(startIndex, endIndex);
      
      return {
        appointments: paginated,
        total: filtered.length,
        page: page,
        limit: limit,
        totalPages: Math.ceil(filtered.length / limit)
      };
    }
    if (endpoint.includes('/appointments/get-last-and-next-appointment')) {
      // Get last and next appointment
      // Endpoint format: /appointments/get-last-and-next-appointment?customerId=...&tenantId=...
      const urlParams = new URLSearchParams(endpoint.split('?')[1] || '');
      const customerId = urlParams.get('customerId');
      const tenantId = urlParams.get('tenantId');
      
      // Filtriraj termine za određenog korisnika i tenanta
      let filtered = allAppointments;
      
      if (customerId) {
        filtered = filtered.filter(apt => {
          const aptCustomerId = apt.customerId?._id || apt.customerId;
          return String(aptCustomerId) === String(customerId);
        });
      }
      
      if (tenantId) {
        filtered = filtered.filter(apt => {
          const aptTenantId = apt.tenantId?._id || apt.tenantId;
          return String(aptTenantId) === String(tenantId);
        });
      }
      
      // Filtriraj samo confirmed termine
      filtered = filtered.filter(apt => apt.status === 'confirmed');
      
      const now = new Date();
      
      // Podeli termine na prošle i buduće
      const pastAppointments = filtered.filter(apt => {
        const aptDate = new Date(apt.appointmentDate);
        if (apt.selectedTime) {
          const [hours, minutes] = apt.selectedTime.split(':').map(v => parseInt(v, 10));
          aptDate.setHours(hours || 0, minutes || 0, 0, 0);
        }
        return aptDate < now;
      });
      
      const futureAppointments = filtered.filter(apt => {
        const aptDate = new Date(apt.appointmentDate);
        if (apt.selectedTime) {
          const [hours, minutes] = apt.selectedTime.split(':').map(v => parseInt(v, 10));
          aptDate.setHours(hours || 0, minutes || 0, 0, 0);
        }
        return aptDate >= now;
      });
      
      // Sortiraj prošle termine opadajuće (najnoviji prvi)
      pastAppointments.sort((a, b) => {
        const dateA = new Date(a.appointmentDate);
        const dateB = new Date(b.appointmentDate);
        if (a.selectedTime) {
          const [hoursA, minutesA] = a.selectedTime.split(':').map(v => parseInt(v, 10));
          dateA.setHours(hoursA || 0, minutesA || 0, 0, 0);
        }
        if (b.selectedTime) {
          const [hoursB, minutesB] = b.selectedTime.split(':').map(v => parseInt(v, 10));
          dateB.setHours(hoursB || 0, minutesB || 0, 0, 0);
        }
        return dateB - dateA; // Opadajuće
      });
      
      // Sortiraj buduće termine rastuće (najraniji prvi)
      futureAppointments.sort((a, b) => {
        const dateA = new Date(a.appointmentDate);
        const dateB = new Date(b.appointmentDate);
        if (a.selectedTime) {
          const [hoursA, minutesA] = a.selectedTime.split(':').map(v => parseInt(v, 10));
          dateA.setHours(hoursA || 0, minutesA || 0, 0, 0);
        }
        if (b.selectedTime) {
          const [hoursB, minutesB] = b.selectedTime.split(':').map(v => parseInt(v, 10));
          dateB.setHours(hoursB || 0, minutesB || 0, 0, 0);
        }
        return dateA - dateB; // Rastuće
      });
      
      return {
        lastAppointment: pastAppointments[0] || null,
        nextAppointment: futureAppointments[0] || null
      };
    }
    // Get appointment by ID - proveri da li je to GET /appointments/:id
    if (endpoint.match(/\/appointments\/[^/]+$/) && !endpoint.includes('/appointments/tenant/') && 
        !endpoint.includes('/appointments/customer/') && !endpoint.includes('/appointments/employee/') &&
        !endpoint.includes('/appointments/stats/') && !endpoint.includes('/appointments/statistics') &&
        !endpoint.includes('/appointments/get-last-and-next-appointment') &&
        !endpoint.includes('/appointments/available-slots')) {
      const appointmentId = endpoint.split('/appointments/')[1]?.split('?')[0];
      const appointment = allAppointments.find(a => a._id === appointmentId);
      if (appointment) {
        return appointment;
      }
      // Ako ne postoji, vrati prvi appointment ili null
      return allAppointments[0] || null;
    }
    // Fallback - vrati sve termine
    return { appointments: allAppointments };
  }

  if (endpoint.includes('/appointments') && method === 'POST') {
    // Create appointment - čuvaj u localStorage
    const newAppointment = demoStorage.addDemoAppointment({
      ...body,
      status: body.status || 'pending'
    });
    return newAppointment;
  }

  if (endpoint.includes('/appointments') && method === 'PATCH') {
    // Update appointment status - ažuriraj u localStorage
    const appointmentId = endpoint.split('/appointments/')[1]?.split('/')[0];
    
    // Proveri da li je cancel endpoint
    if (endpoint.includes('/cancel')) {
      const updated = demoStorage.updateDemoAppointment(appointmentId, {
        status: 'cancelled',
        cancellationReason: body?.cancellationReason || 'Otkazano od strane korisnika',
        cancelledAt: new Date().toISOString()
      });
      return updated || { _id: appointmentId, status: 'cancelled', ...body };
    }
    
    // Proveri da li je confirm endpoint
    if (endpoint.includes('/confirm')) {
      const updated = demoStorage.updateDemoAppointment(appointmentId, {
        status: 'confirmed',
        confirmedAt: new Date().toISOString()
      });
      return updated || { _id: appointmentId, status: 'confirmed' };
    }
    
    // Proveri da li je pending endpoint
    if (endpoint.includes('/pending')) {
      const updated = demoStorage.updateDemoAppointment(appointmentId, {
        status: 'pending'
      });
      return updated || { _id: appointmentId, status: 'pending' };
    }
    
    // Proveri da li je payment endpoint
    if (endpoint.includes('/payment')) {
      const updated = demoStorage.updateDemoAppointment(appointmentId, {
        ...body,
        paymentStatus: body.paymentStatus || 'paid'
      });
      return updated || { _id: appointmentId, ...body };
    }
    
    // Opšti update
    const updated = demoStorage.updateDemoAppointment(appointmentId, {
      ...body,
      status: body.status || 'confirmed'
    });
    return updated || { _id: appointmentId, ...body };
  }

  if (endpoint.includes('/appointments') && method === 'PUT') {
    // Update appointment - ažuriraj u localStorage
    const appointmentId = endpoint.split('/appointments/')[1]?.split('?')[0];
    const updated = demoStorage.updateDemoAppointment(appointmentId, body);
    return updated || { _id: appointmentId, ...body };
  }

  if (endpoint.includes('/appointments') && method === 'DELETE') {
    // Delete appointment - obriši iz localStorage
    const appointmentId = endpoint.split('/appointments/')[1]?.split('?')[0];
    demoStorage.deleteDemoAppointment(appointmentId);
    return { success: true, message: 'Appointment deleted' };
  }

  if (endpoint.includes('/users') && method === 'GET') {
    if (endpoint.includes('/users/tenant/')) {
      // Proveri da li se traže zaposleni ili korisnici na osnovu query parametara
      const urlParams = new URLSearchParams(endpoint.split('?')[1] || '');
      const userType = urlParams.get('userType');
      
      if (userType === 'employee' || userType === 'tenant_admin') {
        const employees = demoStorage.getDemoEmployees();
        return { users: employees };
      } else if (userType === 'customer' || !userType) {
        const customers = demoStorage.getDemoCustomers();
        return { users: customers };
      }
      return { users: [] };
    }
    if (endpoint.includes('/users/type/')) {
      const userType = endpoint.split('/users/type/')[1]?.split('?')[0];
      if (userType === 'employee' || userType === 'tenant_admin') {
        const employees = demoStorage.getDemoEmployees();
        return { users: employees };
      } else if (userType === 'customer') {
        const customers = demoStorage.getDemoCustomers();
        return { users: customers };
      }
      return { users: [] };
    }
    return { users: [] };
  }

  if (endpoint === '/users' && method === 'POST') {
    // Create new user - čuvaj u localStorage
    const userType = body.userType || 'customer';
    if (userType === 'employee' || userType === 'tenant_admin') {
      const newEmployee = demoStorage.addDemoEmployee({
        ...body,
        _id: body._id || `demo-employee-${Date.now()}`,
        createdAt: new Date().toISOString()
      });
      return newEmployee;
    } else {
      // Customer
      const newCustomer = demoStorage.addDemoCustomer({
        ...body,
        _id: body._id || `demo-customer-${Date.now()}`,
        createdAt: new Date().toISOString()
      });
      return newCustomer;
    }
  }

  if (endpoint.includes('/users/') && method === 'PUT') {
    // Update user - ažuriraj u localStorage
    const userId = endpoint.split('/users/')[1]?.split('?')[0];
    const userType = body.userType || 'customer';
    
    if (userType === 'employee' || userType === 'tenant_admin') {
      const updated = demoStorage.updateDemoEmployee(userId, body);
      return updated || { _id: userId, ...body };
    } else {
      const updated = demoStorage.updateDemoCustomer(userId, body);
      return updated || { _id: userId, ...body };
    }
  }

  if (endpoint.includes('/users/') && method === 'DELETE') {
    // Delete user - obriši iz localStorage
    const userId = endpoint.split('/users/')[1]?.split('?')[0];
    // Pokušaj da obrišeš iz oba (employee ili customer)
    demoStorage.deleteDemoEmployee(userId);
    demoStorage.deleteDemoCustomer(userId);
    return { success: true, message: 'User deleted' };
  }

  if (endpoint.includes('/exceptions') && method === 'GET') {
    const allExceptions = demoStorage.getDemoExceptions();
    if (endpoint.includes('/exceptions/tenant/')) {
      // Get exceptions for tenant
      return { 
        data: {
          exceptions: allExceptions,
          total: allExceptions.length,
          page: 1,
          limit: 10
        }
      };
    }
    if (endpoint.includes('/exceptions/')) {
      // Get exception by ID
      const exceptionId = endpoint.split('/exceptions/')[1]?.split('?')[0];
      const exception = allExceptions.find(e => e._id === exceptionId);
      return { data: exception || null };
    }
    return { data: { exceptions: allExceptions } };
  }

  if (endpoint.includes('/exceptions') && method === 'POST') {
    // Create exception - čuvaj u localStorage
    const newException = demoStorage.addDemoException(body);
    return { data: newException };
  }

  if (endpoint.includes('/exceptions') && method === 'PUT') {
    // Update exception - ažuriraj u localStorage
    const exceptionId = endpoint.split('/exceptions/')[1]?.split('?')[0];
    const updated = demoStorage.updateDemoException(exceptionId, body);
    return { data: updated || { _id: exceptionId, ...body } };
  }

  if (endpoint.includes('/exceptions') && method === 'DELETE') {
    // Delete exception - obriši iz localStorage
    const exceptionId = endpoint.split('/exceptions/')[1]?.split('?')[0];
    demoStorage.deleteDemoException(exceptionId);
    return { data: { success: true } };
  }

  if (endpoint.includes('/exceptions/check-conflicts') && method === 'POST') {
    // Check conflicts - simuliraj da nema konflikata
    return { data: { hasConflicts: false, conflictingAppointments: [] } };
  }

  if (endpoint.includes('/exceptions/with-confirmation') && method === 'POST') {
    // Create exception with confirmation - simuliraj uspeh
    const newException = {
      _id: `demo-exception-${Date.now()}`,
      ...body.exceptionData,
      createdAt: new Date().toISOString()
    };
    return { data: newException };
  }

  // Default mock response
  return { success: true, message: 'Demo mode - mock response' };
};
